export const EDITOR_ROUTE_PATHS = {
  ROOT: '',
  HOME: {
    ROOT: 'home',
    CHILDREN: {
      INTRODUCTION: 'introduction',
      PROGRESS_OVERVIEW: 'progress-overview',
      WATCHLIST: 'watchlist',
    },
  },
  INVENTORIES: {
    ROOT: 'inventories',
    CHILDREN: {
      ACTIVITY_INVENTORIES: 'activity-inventories',
      EXPORT_IMPORT: 'export-import',
      REPORTING: 'reporting',
      GOAL: 'goal',
      RESOURCE_BALANCE: 'resource-balance',
      HISTORICAL_EMISSIONS: {
        ROOT: 'historical-emissions',
        CHILDREN: {
          EMISSION_VALUES: 'emission-values',
          NODES: 'nodes',
        },
      },
      ECONOMICS: 'economics',
      SOCIOECONOMIC_PARAMETERS: 'socioeconomic-parameters',
    },
  },
  TRANSITIONS: {
    ROOT: 'transitions',
    CHILDREN: {
      TRANSITION_TARGETS: 'transition-targets',
      TARGET_BALANCE: 'target-balance',
      ENERGY_MODEL: 'energy-model',
      ACTIVITY_OUTCOME: 'activity-outcome',
      TRANSITION_HIERARCHY: 'transition-hierarchy',
    },
  },
  INTERVENTIONS: {
    ROOT: 'interventions',
    CHILDREN: {
      INTERVENTIONS: 'interventions',
      INTERVENTION_LIBRARIES: 'intervention-libraries',
      EXPORT_IMPORT: 'export-import',
    },
  },
  INVESTMENT_PLAN: 'investment-plan',
  DASHBOARD: {
    ROOT: 'dashboard',
    CHILDREN: {
      PREVIEW: {
        ROOT: 'preview',
        CHILDREN: {
          DASHBOARD: 'dashboard',
        },
      },
      EMISSION_STORIES: 'emission-stories',
      TRANSITION_STORIES: 'transition-stories',
    },
  },
  SETTINGS: {
    ROOT: 'settings',
  },
} as const;

export const EDITOR_ROUTE_PATH_TENANT = 'organisation';
export const EDITOR_ROUTE_PATH_BOARD = 'board';

export const APP_ROUTE_PATHS = {
  COOKIE_NOTICE: 'cookie-notice',
  EDITOR: {
    ROOT: '',
    EDITOR: {
      ROOT: `${EDITOR_ROUTE_PATH_TENANT}/:${EDITOR_ROUTE_PATH_TENANT}Id/${EDITOR_ROUTE_PATH_BOARD}/:${EDITOR_ROUTE_PATH_BOARD}Id`,
      CHILDREN: EDITOR_ROUTE_PATHS,
    },
    SIGN_IN: 'sign-in',
    NO_MEMBERSHIP: 'no-membership',
    ADMIN: {
      ROOT: 'admin',
      CHILDREN: {
        CITY_PROGRESS: 'city-progress',
        ORGANISATIONS: 'organisations',
        TRANSITION_TABLE: 'transition-table',
        TRANSITION_ELEMENT_TABLE: 'transition-element-table',
      },
    },
  },
  PUBLIC: {
    ROOT: 'public',
    BOARD: 'board',
    V3: 'v3/public',
    V4: 'v4/public',
  },
  NOT_FOUND: '**',
  SWEDEN: 'sweden',
  EXTERNAL_ROUTES: {
    PANORAMA_SE_URL: 'https://panorama-sverige.se',
    PANORAMA_EN_URL: 'https://panorama-sweden.com',
  },
} as const;

export type EditorBasePath =
  | (typeof APP_ROUTE_PATHS)['EDITOR']['ROOT']
  | (typeof APP_ROUTE_PATHS)['EDITOR']['EDITOR']['ROOT']
  | (typeof APP_ROUTE_PATHS)['EDITOR']['SIGN_IN']
  | (typeof APP_ROUTE_PATHS)['EDITOR']['NO_MEMBERSHIP']
  | (typeof APP_ROUTE_PATHS)['EDITOR']['ADMIN']['ROOT'];

export type EditorTabPath = {
  [K in keyof typeof EDITOR_ROUTE_PATHS]: (typeof EDITOR_ROUTE_PATHS)[K] extends string
    ? (typeof EDITOR_ROUTE_PATHS)[K]
    : 'ROOT' extends keyof (typeof EDITOR_ROUTE_PATHS)[K]
      ? (typeof EDITOR_ROUTE_PATHS)[K]['ROOT']
      : never;
}[keyof typeof EDITOR_ROUTE_PATHS];

export type EditorSubTabPath = {
  [K in keyof typeof EDITOR_ROUTE_PATHS]: 'CHILDREN' extends keyof (typeof EDITOR_ROUTE_PATHS)[K]
    ? (typeof EDITOR_ROUTE_PATHS)[K]['CHILDREN'][keyof (typeof EDITOR_ROUTE_PATHS)[K]['CHILDREN']]
    : never;
}[keyof typeof EDITOR_ROUTE_PATHS];

export const NAV_ROUTES = {
  NO_MEMBERSHIP: APP_ROUTE_PATHS.EDITOR.NO_MEMBERSHIP,
  SIGN_IN: APP_ROUTE_PATHS.EDITOR.SIGN_IN,
  ADMIN: APP_ROUTE_PATHS.EDITOR.ADMIN,
  BOARD_EDITOR_MULTI_YEAR_INVENTORY: (tenantId: string, boardId: string): string =>
    `/${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.ACTIVITY_INVENTORIES}`,
  BOARD_EDITOR_HISTORICAL_EMISSIONS_DEPRECATED: (tenantId: string, boardId: string): string =>
    `/${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.HISTORICAL_EMISSIONS.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.HISTORICAL_EMISSIONS.CHILDREN.NODES}`,
  BOARD_EDITOR_HISTORICAL_EMISSIONS: (tenantId: string, boardId: string): string =>
    `/${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.HISTORICAL_EMISSIONS.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.HISTORICAL_EMISSIONS.CHILDREN.EMISSION_VALUES}`,
  BOARD_EDITOR_HOME_SCREEN: (tenantId: string, boardId: string): string =>
    `${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.HOME.ROOT}`,
  BOARD_EDITOR_HOME_SCREEN_INTRODUCTION: (tenantId: string, boardId: string): string =>
    `${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.HOME.ROOT}/${EDITOR_ROUTE_PATHS.HOME.CHILDREN.INTRODUCTION}`,
  BOARD_EDITOR_TRANSITION_HIERARCHY: (tenantId: string, boardId: string): string =>
    `${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT}/${EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.TRANSITION_HIERARCHY}`,
  BOARD_EDITOR_GOAL: (tenantId: string, boardId: string): string =>
    `${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.INVENTORIES.ROOT}/${EDITOR_ROUTE_PATHS.INVENTORIES.CHILDREN.GOAL}`,
  PUBLIC_LINK: (boardId: string): string =>
    `/${APP_ROUTE_PATHS.PUBLIC.ROOT}/${APP_ROUTE_PATHS.PUBLIC.BOARD}/${boardId}`,
  V3_SELF_CONTAINED: (boardId: string): string =>
    `${APP_ROUTE_PATHS.PUBLIC.V3}/${APP_ROUTE_PATHS.PUBLIC.BOARD}/${boardId}`,
  V4_SELF_CONTAINED: (boardId: string): string =>
    `${APP_ROUTE_PATHS.PUBLIC.V4}/${APP_ROUTE_PATHS.PUBLIC.BOARD}/${boardId}`,
  ROOT: (tenantId: string, boardId: string) =>
    `${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/`,
  TENANT_SETTINGS: (tenantId: string, boardId: string): string =>
    `${EDITOR_ROUTE_PATH_TENANT}/${tenantId}/${EDITOR_ROUTE_PATH_BOARD}/${boardId}/${EDITOR_ROUTE_PATHS.SETTINGS.ROOT}`,

  EXTERNAL_ROUTES: {
    PANORAMA_SE_URL: APP_ROUTE_PATHS.EXTERNAL_ROUTES.PANORAMA_SE_URL,
    PANORAMA_EN_URL: APP_ROUTE_PATHS.EXTERNAL_ROUTES.PANORAMA_EN_URL,
  },
};
